/** @fileoverview Loads an app definition and renders it in the preview route. */

import { useEffect, useState } from 'react';
import AccountMenu from '../components/AccountMenu';
import FabrikAppRenderer from '../components/FabrikAppRenderer';
import AppFrame from '../components/AppRenderer/AppFrame';
import LoadingState from '../components/ui/loading-state';
import { useLockDocumentScroll } from '../hooks/useLockDocumentScroll';
import { isRecord } from '../lib/utils';
import { createPreviewSelectionController } from '../lib/previewSelectionController';
import { getFabrikApp, getFabrikAppAppearance } from '../services/appsApi';
import type { FabrikAppDefinition, FabrikResolvedAppearance } from '../types/apps';
import type { Tenant, User } from '../types/identity';

type Props = {
  tenantId: string;
  appId: string;
  tenant: Tenant;
  user?: User;
};

const isAppDefinition = (value: unknown): value is FabrikAppDefinition =>
  isRecord(value) &&
  value.schemaVersion === 3 &&
  isRecord(value.app) &&
  typeof value.app.name === 'string' &&
  Array.isArray(value.screens);

export default function AppPreviewPage({ tenantId, appId, tenant, user }: Props) {
  const [definition, setDefinition] = useState<FabrikAppDefinition>();
  const [appearance, setAppearance] = useState<FabrikResolvedAppearance>();
  const [error, setError] = useState('');

  useLockDocumentScroll();

  useEffect(() => {
    const controller = new AbortController();
    const selection = createPreviewSelectionController(appId);
    let receivedDraftUpdate = false;
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== window.parent ||
        !isRecord(event.data)
      ) {
        return;
      }

      const message = event.data;
      if (selection.handleMessage(message)) return;
      if (
        message.type !== 'fabrik:app-draft-updated' ||
        message.appId !== appId ||
        !isAppDefinition(message.definition)
      ) {
        return;
      }

      receivedDraftUpdate = true;
      controller.abort();
      setDefinition(message.definition);
      getFabrikAppAppearance(tenantId, appId)
        .then((result) => setAppearance(result.effective))
        .catch(() => undefined);
      setError('');
    };

    getFabrikAppAppearance(tenantId, appId, controller.signal)
      .then((result) => setAppearance(result.effective))
      .catch(() => undefined);
    getFabrikApp(tenantId, appId, controller.signal)
      .then((app) => {
        if (receivedDraftUpdate) return;
        if (app.draft_definition) setDefinition(app.draft_definition);
        else setError('This app has no saved definition.');
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error ? reason.message : 'Unable to load this app.');
        }
      });
    window.addEventListener('message', handleMessage);
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'fabrik:preview-ready', appId }, window.location.origin);
    }

    return () => {
      controller.abort();
      window.removeEventListener('message', handleMessage);
      selection.dispose();
    };
  }, [appId, tenantId]);

  return (
    <AppFrame
      theme={{
        ...definition?.theme,
        primaryColor: definition?.theme?.primaryColor || appearance?.primaryColor,
      }}
      screens={definition?.screens}
      navigation={definition?.navigation}
      tenantName={tenant.name}
      appName={definition?.app.name || 'App preview'}
      appearance={
        appearance ||
        (definition
          ? {
              displayName: tenant.name,
              primaryColor: definition.theme?.primaryColor || '#0D6B8A',
              logo: null,
            }
          : undefined)
      }
      accountSlot={
        user && (
          <div className="ml-auto flex items-center gap-2">
            <AccountMenu user={user} tenant={tenant} closed />
          </div>
        )
      }
      centered={!definition || Boolean(error)}
    >
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : definition ? (
        <FabrikAppRenderer
          definition={definition}
          tenantId={tenantId}
          appId={appId}
          viewer={user}
        />
      ) : (
        <LoadingState>Loading app preview…</LoadingState>
      )}
    </AppFrame>
  );
}
